import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { optionalIntegerString, parseIntegerString } from "../units";
import { fetchJson } from "./client";
import { immutableCache } from "./cacheClasses";
import { queryKeys } from "./queryKeys";
import { isValidBlockHeight, isValidDomainId } from "./validation";

// explorer-api serializes with JsonIgnoreCondition.WhenWritingNull, so nullable
// fields are absent (not `null`) on the wire. Hydrators treat undefined as
// the absent-value sentinel — see CLAUDE.md "API client".

interface WireDomainDetailResponse {
  readonly domainId: number;
  readonly toHeight: string;
  readonly name?: string;
  readonly registeredAtHeight: string;
  readonly registeredAtBlockTime?: string;
  readonly ownerScriptPubKey?: string;
}

interface WireMiningSeriesEntry {
  readonly height: string;
  readonly blockTime?: string;
  readonly txid: string;
  readonly txIndex: number;
  readonly gatesPass: boolean;
  readonly canonicalBlend?: string;
  readonly rewardRank?: number;
  readonly rewardCogtoshi: string;
  readonly feeSats?: string;
}

interface WireMiningSeriesResponse {
  readonly domainId: number;
  readonly fromHeight?: string;
  readonly toHeight: string;
  readonly entries: readonly WireMiningSeriesEntry[];
}

interface WireSentencesEntry {
  readonly height: string;
  readonly blockTime?: string;
  readonly txid: string;
  readonly txIndex: number;
  readonly sentenceText?: string;
  readonly rawSentenceBytes: string;
  readonly gatesPass: boolean;
  readonly canonicalBlend?: string;
  readonly rewardRank?: number;
  readonly rewardCogtoshi: string;
}

interface WireSentencesResponse {
  readonly domainId: number;
  readonly toHeight: string;
  readonly entries: readonly WireSentencesEntry[];
  readonly nextCursor?: string;
}

interface WireMiningEfficiencyResponse {
  readonly domainId: number;
  readonly fromHeight?: string;
  readonly toHeight: string;
  readonly submissions: number;
  readonly wins: number;
  readonly gateFailures: number;
  readonly cogtoshiWon: string;
  readonly satsSpent: string;
}

export interface DomainDetail {
  readonly domainId: number;
  readonly toHeight: bigint;
  readonly name: string | null;
  readonly registeredAtHeight: bigint;
  readonly registeredAtBlockTime: bigint | null;
  readonly ownerScriptPubKey: string | null;
}

export interface MiningSeriesEntry {
  readonly height: bigint;
  readonly blockTime: bigint | null;
  readonly txid: string;
  readonly txIndex: number;
  readonly gatesPass: boolean;
  readonly canonicalBlend: bigint | null;
  readonly rewardRank: number | null;
  readonly rewardCogtoshi: bigint;
  readonly feeSats: bigint | null;
}

export interface DomainMiningSeries {
  readonly domainId: number;
  readonly fromHeight: bigint | null;
  readonly toHeight: bigint;
  readonly entries: readonly MiningSeriesEntry[];
}

export interface DomainSentenceEntry {
  readonly height: bigint;
  readonly blockTime: bigint | null;
  readonly txid: string;
  readonly txIndex: number;
  readonly sentenceText: string | null;
  readonly rawSentenceBytes: string;
  readonly gatesPass: boolean;
  readonly canonicalBlend: bigint | null;
  readonly rewardRank: number | null;
  readonly rewardCogtoshi: bigint;
}

export interface DomainSentencesPage {
  readonly domainId: number;
  readonly toHeight: bigint;
  readonly entries: readonly DomainSentenceEntry[];
  readonly nextCursor: string | null;
}

export interface DomainMiningEfficiency {
  readonly domainId: number;
  readonly fromHeight: bigint | null;
  readonly toHeight: bigint;
  readonly submissions: number;
  readonly wins: number;
  readonly gateFailures: number;
  readonly cogtoshiWon: bigint;
  readonly satsSpent: bigint;
}

export function hydrateDomainDetail(wire: WireDomainDetailResponse): DomainDetail {
  return {
    domainId: wire.domainId,
    toHeight: parseIntegerString(wire.toHeight),
    name: wire.name ?? null,
    registeredAtHeight: parseIntegerString(wire.registeredAtHeight),
    registeredAtBlockTime: optionalIntegerString(wire.registeredAtBlockTime),
    ownerScriptPubKey: wire.ownerScriptPubKey ?? null,
  };
}

export function hydrateDomainMiningSeries(
  wire: WireMiningSeriesResponse,
): DomainMiningSeries {
  return {
    domainId: wire.domainId,
    fromHeight: optionalIntegerString(wire.fromHeight),
    toHeight: parseIntegerString(wire.toHeight),
    entries: wire.entries.map((e) => ({
      height: parseIntegerString(e.height),
      blockTime: optionalIntegerString(e.blockTime),
      txid: e.txid,
      txIndex: e.txIndex,
      gatesPass: e.gatesPass,
      canonicalBlend: optionalIntegerString(e.canonicalBlend),
      rewardRank: e.rewardRank ?? null,
      rewardCogtoshi: parseIntegerString(e.rewardCogtoshi),
      feeSats: optionalIntegerString(e.feeSats),
    })),
  };
}

export function hydrateDomainSentencesPage(
  wire: WireSentencesResponse,
): DomainSentencesPage {
  return {
    domainId: wire.domainId,
    toHeight: parseIntegerString(wire.toHeight),
    entries: wire.entries.map((e) => ({
      height: parseIntegerString(e.height),
      blockTime: optionalIntegerString(e.blockTime),
      txid: e.txid,
      txIndex: e.txIndex,
      sentenceText: e.sentenceText ?? null,
      rawSentenceBytes: e.rawSentenceBytes,
      gatesPass: e.gatesPass,
      canonicalBlend: optionalIntegerString(e.canonicalBlend),
      rewardRank: e.rewardRank ?? null,
      rewardCogtoshi: parseIntegerString(e.rewardCogtoshi),
    })),
    nextCursor: wire.nextCursor ?? null,
  };
}

export function hydrateDomainMiningEfficiency(
  wire: WireMiningEfficiencyResponse,
): DomainMiningEfficiency {
  return {
    domainId: wire.domainId,
    fromHeight: optionalIntegerString(wire.fromHeight),
    toHeight: parseIntegerString(wire.toHeight),
    submissions: wire.submissions,
    wins: wire.wins,
    gateFailures: wire.gateFailures,
    cogtoshiWon: parseIntegerString(wire.cogtoshiWon),
    satsSpent: parseIntegerString(wire.satsSpent),
  };
}

export function useDomainDetail(
  domainId: number | null,
  toHeight: number | null,
): UseQueryResult<DomainDetail> {
  return useQuery({
    queryKey: queryKeys.domain.detail(domainId ?? -1, toHeight ?? -1),
    queryFn: async () => {
      const path = `/api/v1/domains/${(domainId ?? 0).toString()}/${(toHeight ?? 0).toString()}`;
      return hydrateDomainDetail(await fetchJson<WireDomainDetailResponse>(path));
    },
    enabled: isValidDomainId(domainId) && isValidBlockHeight(toHeight),
    ...immutableCache,
  });
}

export function useDomainMiningSeries(
  domainId: number | null,
  toHeight: number | null,
  fromHeight: number | null = null,
): UseQueryResult<DomainMiningSeries> {
  return useQuery({
    queryKey: queryKeys.domain.miningSeries(domainId ?? -1, toHeight ?? -1, fromHeight),
    queryFn: async () => {
      const base = `/api/v1/domains/${(domainId ?? 0).toString()}/mining/series/${(toHeight ?? 0).toString()}`;
      const path =
        fromHeight === null ? base : `${base}?fromHeight=${fromHeight.toString()}`;
      return hydrateDomainMiningSeries(await fetchJson<WireMiningSeriesResponse>(path));
    },
    enabled: isValidDomainId(domainId) && isValidBlockHeight(toHeight),
    ...immutableCache,
  });
}

export interface SentencesPageOptions {
  readonly cursor?: string | null;
  readonly limit?: number;
}

export function useDomainSentencesPage(
  domainId: number | null,
  toHeight: number | null,
  options: SentencesPageOptions = {},
): UseQueryResult<DomainSentencesPage> {
  const cursor = options.cursor ?? null;
  const limit = options.limit;
  return useQuery({
    queryKey: queryKeys.domain.sentencesPage(
      domainId ?? -1,
      toHeight ?? -1,
      cursor,
      limit ?? null,
    ),
    queryFn: async () => {
      const base = `/api/v1/domains/${(domainId ?? 0).toString()}/sentences/${(toHeight ?? 0).toString()}`;
      const params = new URLSearchParams();
      if (cursor !== null && cursor !== "") params.set("cursor", cursor);
      if (limit !== undefined) params.set("limit", limit.toString());
      const qs = params.toString();
      const path = qs.length === 0 ? base : `${base}?${qs}`;
      return hydrateDomainSentencesPage(await fetchJson<WireSentencesResponse>(path));
    },
    enabled: isValidDomainId(domainId) && isValidBlockHeight(toHeight),
    ...immutableCache,
  });
}

export function useDomainMiningEfficiency(
  domainId: number | null,
  toHeight: number | null,
  fromHeight: number | null = null,
): UseQueryResult<DomainMiningEfficiency> {
  return useQuery({
    queryKey: queryKeys.domain.miningEfficiency(domainId ?? -1, toHeight ?? -1, fromHeight),
    queryFn: async () => {
      const base = `/api/v1/domains/${(domainId ?? 0).toString()}/mining/efficiency/${(toHeight ?? 0).toString()}`;
      const path =
        fromHeight === null ? base : `${base}?fromHeight=${fromHeight.toString()}`;
      return hydrateDomainMiningEfficiency(
        await fetchJson<WireMiningEfficiencyResponse>(path),
      );
    },
    enabled: isValidDomainId(domainId) && isValidBlockHeight(toHeight),
    ...immutableCache,
  });
}
